import createAppSlice, { initialState as appInitialState } from "@/store/app"
import createBoiloutSlice, {
  initialState as boiloutInitialState,
} from "@/store/boilout"
import createIPQCSlice, { initialState as ipqcInitialState } from "@/store/ipqc"
import createPackagingSlice, {
  initialState as packagingInitialState,
} from "@/store/packaging"
import createPackagingV2Slice, {
  initialState as packagingV2InitialState,
} from "@/store/packaging-v2"
import createRMPMSlice, { initialState as rmpmInitialState } from "@/store/rmpm"
import createSessionSlice, {
  initialState as sessionInitialState,
} from "@/store/session"
import createUserSlice, { initialState as userInitialState } from "@/store/user"
import { createSelectors, Store } from "@/store/utils"
import { setAutoFreeze } from "immer"
import { cloneDeep, merge, omit } from "lodash"
import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"

// This is needed so that we don't get
// read-only object errors
setAutoFreeze(false)

const useStore = createSelectors(
  create<Store>()(
    devtools(
      immer(
        persist(
          (...a) => {
            return {
              ...createIPQCSlice(...a),
              ...createBoiloutSlice(...a),
              ...createUserSlice(...a),
              ...createAppSlice(...a),
              ...createPackagingSlice(...a),
              ...createPackagingV2Slice(...a),
              ...createSessionSlice(...a),
              ...createRMPMSlice(...a),
              reset() {
                const [__set, __get, store] = a
                const initialState = store.getInitialState()
                const currState = store.getState()

                store.setState({
                  ...initialState,
                  user: {
                    ...initialState.user,
                    ...userInitialState(),
                  },
                  ipqc: {
                    ...initialState.ipqc,
                    ...ipqcInitialState(),
                  },
                  boilout: {
                    ...initialState.boilout,
                    ...boiloutInitialState(),
                  },
                  app: {
                    ...initialState.app,
                    ...appInitialState(),
                    version: currState.app.version,
                  },
                  packaging: {
                    ...initialState.packaging,
                    ...packagingInitialState(),
                  },
                  session: {
                    ...initialState.session,
                    ...sessionInitialState(),
                  },
                  rmpm: {
                    ...initialState.rmpm,
                    ...rmpmInitialState(),
                  },
                  packagingV2: {
                    ...initialState.packagingV2,
                    ...packagingV2InitialState(),
                  },
                })
              },
            }
          },
          {
            name: "app-store",
            version: 25, // Bump version to force migration
            // Exclude ipqc slice from persistence - IPQC data should always be fetched fresh from server
            partialize: (state) => omit(state, ["ipqc", "boilout"]),
            migrate(persistedState) {
              // Return persisted state as-is for migration
              return persistedState as Omit<Store, "ipqc">
            },
            merge(persistedState, currentState) {
              const result = cloneDeep(currentState)

              return merge(result, persistedState)
            },
          }
        )
      )
    )
  )
)

export default useStore
